/**
 * IntegrityClient - communicates with /api/adr/script-integrity
 *
 * Payload: script_url (required), script_hash (required for first_party; null for third_party).
 * Optional: normalized_script_url, source, can_hash, hash_status .
 */
export class IntegrityClient {
	constructor(config, logger, options = {}) {
		this.config = config;
		this.logger = logger;
		/** @type {import('../auth/session-token-manager.js').SessionTokenManager|null} */
		this.tokenManager = options.tokenManager || null;
		/** @type {import('../core/sensor-integrity.js').SensorIntegrity|null} */
		this.sensorIntegrity = options.sensorIntegrity || null;
	}

	getEndpoint() {
		return this.config?.get?.('scriptIntegrityEndpoint') || '/api/adr/script-integrity';
	}

	async _getSensorHash() {
		try {
			if (this.sensorIntegrity && typeof this.sensorIntegrity.getSensorHash === 'function') {
				return await this.sensorIntegrity.getSensorHash();
			}
		} catch {
		}
		return (this.config?.get?.('sensorHash')) || null;
	}

	async _makeRequest(body, options = {}) {
		const headers = {
			'Content-Type': 'application/json',
			'Accept': 'application/json'
		};

		if (this.tokenManager) {
			const jwt = options && options._forceFreshToken
				? await this.tokenManager.forceRefresh()
				: await this.tokenManager.getToken();
			headers['Authorization'] = `Bearer ${jwt}`;
		}

		const sensorHash = await this._getSensorHash();
		if (sensorHash) {
			headers['X-Sensor-Integrity'] = sensorHash;
		}

		const timeout = Number(this.config?.get?.('timeout')) || 15000;
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), timeout);

		try {
			const response = await fetch(this.getEndpoint(), {
				method: 'POST',
				headers,
				body: JSON.stringify(body),
				signal: controller.signal
			});
			clearTimeout(timeoutId);
			return response;
		} catch (error) {
			clearTimeout(timeoutId);
			if (error.name === 'AbortError') {
				throw new Error('Script integrity request timeout');
			}
			throw error;
		}
	}

	async report(payload) {
		const { script_url, script_hash, normalized_script_url, source, can_hash, hash_status } = payload ?? {};
		if (!script_url) return null;
		const isThirdParty = source === 'third_party';
		if (!isThirdParty && script_hash == null) return null;

		const body = {
			script_url,
			script_hash: script_hash ?? null
		};
		if (normalized_script_url !== undefined) body.normalized_script_url = normalized_script_url;
		if (source !== undefined) body.source = source;
		if (can_hash !== undefined) body.can_hash = can_hash;
		if (hash_status !== undefined && script_hash == null) body.hash_status = hash_status;

		try {
			let res = await this._makeRequest(body);

			if ((res.status === 401 || res.status === 403) && this.tokenManager) {
				try {
					await this.tokenManager.forceRefresh();
					res = await this._makeRequest(body, { _forceFreshToken: false });
				} catch {
				}
			}

			const json = await res.json().catch(() => null);
			if (!res.ok) {
				this.logger?.warn?.('Script integrity report failed', { status: res.status, body: json });
				return null;
			}
			return json;
		} catch (err) {
			this.logger?.warn?.('Script integrity report error', err);
			return null;
		}
	}
}

export default IntegrityClient;

